import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';

import { AlertController, IonItemSliding, ModalController, ToastController } from '@ionic/angular';

import { AssetInformationModalPage } from '@modals/asset-information-modal/asset-information-modal.page';
import { ProjectSelectModalPage } from '../project-select-modal/project-select-modal.page';
import { ModalRoutingBasePage } from '@components/modal-routing-base/modal-routing-base.page';

import { ApiService } from '@services/api.service';

import { IAsset, IAssetInformation, IScannedAsset, ITaFormlyTemplateOptions } from '@models/types';

@Component({
    selector: 'app-multi-project-select',
    templateUrl: './multi-project-select.html',
    styleUrls: ['./multi-project-select.scss'],
})
export class MultiProjectSelectComponent implements OnInit {

    @Input() scannedAssets: IScannedAsset[] = [];
    @Input() props: ITaFormlyTemplateOptions;
    @Input() key: string | number | string[];
    @Output() projectsEvent: EventEmitter<IScannedAsset[]> = new EventEmitter();

    public assetLabel = '';
    private title = '';

    public isNFCEnabled = true;
    public isNFCSupported = true;

    private assetFormConfigId: string;
    private identifierType: 'asset_identifier' | 'rfid_tag' | 'qr_code' | 'uhf_rfid_tag';

    constructor(
        private alertController: AlertController,
        private apiService: ApiService,
        private changeDetectorRef: ChangeDetectorRef,
        private modalController: ModalController,
        private toastController: ToastController
    ) { }

    async ngOnInit() {
        if (!('selectEnabled' in this.props)) {
            this.props['selectEnabled'] = true;
        }
        if (!('inputEnabled' in this.props)) {
            this.props['inputEnabled'] = false;
        }

        await this.getProjectAssetFormConfig();
    }

    private async getProjectAssetFormConfig() {
        const projectFormConfig = (await this.apiService.getProjectFormConfig())[0];
        this.assetFormConfigId = projectFormConfig.id;
        this.assetLabel = projectFormConfig.sections[0]?.questions[0]?.fields[0]?.templateOptions?.label;
        this.title = projectFormConfig.title;
        this.changeDetectorRef.detectChanges();
    }

    public async presentProjectsSelectModal() {
        const projectSelectModal = await this.modalController.create({
            component: ProjectSelectModalPage,
            componentProps: {
                multi: this.props.multi,
                currentlySelectedProjects: this.scannedAssets.map(selectedProject => selectedProject.asset_id),
                projectFormConfigId: this.assetFormConfigId
            },
            canDismiss: true
        });
        await projectSelectModal.present();

        const { data } = await projectSelectModal.onWillDismiss();
        if (data) {
            this.scannedAssets.push(...data);
            this.saveAssets();
        }
    }

    public async presentInputTextPrompt(inputValue?: string) {
        const METHOD = 'presentInputTextPrompt';
        // console.log(TAG, METHOD, 'inputValue=', inputValue);
        this.identifierType = 'asset_identifier';
        const prompt = await this.alertController.create({
            header: this.title,
            inputs: [
                {
                    name: 'asset_identifier',
                    placeholder: 'Enter ' + this.assetLabel,
                    value: inputValue || ''
                },
            ],
            buttons: [
                {
                    text: 'Cancel',
                    role: 'cancel'
                },
                {
                    text: 'Submit',
                    handler: async (data) => {
                        if (data && data.asset_identifier) {
                            this.processAssetIdentifier(data.asset_identifier);
                        } else {
                            const toast = await this.toastController.create({ header: 'Value cannot be empty', color: 'danger', position: 'top', duration: 2000 })
                            toast.present();
                            return false;
                        }
                    }
                }
            ],
            backdropDismiss: false
        });
        await prompt.present();
    }

    public async scanQrCode(inputValue?: string) {
        this.identifierType = 'qr_code';
        const prompt = await this.alertController.create({
            header: this.title,
            inputs: [
                {
                    name: 'qr_code',
                    placeholder: 'Enter QR Code',
                    value: inputValue || ''
                },
            ],
            buttons: [
                {
                    text: 'Cancel',
                    role: 'cancel'
                },
                {
                    text: 'Submit',
                    handler: async (data) => {
                        if (data && data.qr_code) {
                            this.processAssetIdentifier(data.qr_code);
                        } else {
                            const toast = await this.toastController.create({ header: 'Value cannot be empty', color: 'danger', position: 'top', duration: 2000, })
                            toast.present();
                            return false;
                        }
                    }
                }
            ],
            backdropDismiss: false
        });
        await prompt.present();
    }

    public async startScanRfidTag(inputValue?: string) {
        this.identifierType = 'rfid_tag';
        const prompt = await this.alertController.create({
            header: this.title,
            inputs: [
                {
                    name: 'rfid_tag',
                    placeholder: 'Enter RFID Tag',
                    value: inputValue || ''
                },
            ],
            buttons: [
                {
                    text: 'Cancel',
                    role: 'cancel'
                },
                {
                    text: 'Submit',
                    handler: async (data) => {
                        if (data && data.rfid_tag) {
                            this.processAssetIdentifier(data.rfid_tag);
                        } else {
                            const toast = await this.toastController.create({ header: 'Value cannot be empty', color: 'danger', position: 'top', duration: 2000, })
                            toast.present();
                            return false;
                        }
                    }
                }
            ],
            backdropDismiss: false
        });
        await prompt.present();
    }

    public async presentUhfRfidInputTextPrompt(inputValue?: string) {
        this.identifierType = 'uhf_rfid_tag';
        const prompt = await this.alertController.create({
            header: 'Scan UHF RFID Tag',
            inputs: [
                {
                    name: 'uhf_rfid_tag',
                    placeholder: 'UHF RFID Tag',
                    value: inputValue || ''
                },
            ],
            buttons: [
                {
                    text: 'Cancel',
                    role: 'cancel'
                },
                {
                    text: 'Submit',
                    handler: async (data) => {
                        if (data && data.uhf_rfid_tag) {
                            this.processAssetIdentifier(data.uhf_rfid_tag);
                        } else {
                            const toast = await this.toastController.create({ header: 'Value cannot be empty', color: 'danger', position: 'top', duration: 2000, })
                            toast.present();
                            return false;
                        }
                    }
                }
            ],
            backdropDismiss: false
        });
        await prompt.present();
    }

    public async presentMultiAssetInformationModal(assets: IAsset[]) {
        const multiAssetInformationModal = await this.modalController.create({
            component: ModalRoutingBasePage,
            componentProps: {
                rootPage: AssetInformationModalPage,
                data: {
                    assets,
                    multi: false,
                    type: 'view',
                    isQuestion: true
                },
            },
            canDismiss: true
        });
        await multiAssetInformationModal.present();

        const response = await multiAssetInformationModal.onDidDismiss();
        if (response.data) {
            const assetId = response.data;
            if (this.scannedAssets.find(a => a.asset_id.toLowerCase().trim() === assetId)) {
                await this.presentAlreadyScannedAlert();
            } else {
                const asset = assets.find(a => a.id === assetId);
                this.confirmAsset(assetId, asset);
            }
        }
    }

    public async presentAssetInformationModal(assetId: string, itemSliding?: IonItemSliding) {
        const asset = await this.apiService.getAsset(assetId);
        const state: Partial<IAssetInformation> = {
            assetId,
            asset,
            multi: this.props.multi,
            type: 'view',
            pageType: 'modal'
        }
        const assetInformationModal = await this.modalController.create({
            component: AssetInformationModalPage,
            componentProps: {
                state
            },
            canDismiss: true,
        });

        if (itemSliding) {
            itemSliding.close();
        }
        await assetInformationModal.present();
    }

    public async presentDeleteAlert(currentProjectIndex: number, itemSliding?: IonItemSliding) {
        const currentProject = this.scannedAssets[currentProjectIndex];
        const deleteProjectAlert = await this.alertController.create({
            header: currentProject.value,
            message: 'Do you want to remove this project?',
            buttons: [
                {
                    text: 'Cancel',
                    role: 'cancel'
                },
                {
                    text: 'Delete',
                    handler: () => {
                        this.scannedAssets.splice(currentProjectIndex, 1);
                        if (itemSliding) {
                            itemSliding.close();
                        }
                        this.saveAssets();
                    }
                }
            ],
            backdropDismiss: false
        });
        await deleteProjectAlert.present();
    }

    private async processAssetIdentifier(identifierValue: string) {
        const assets: IAsset[] = await this.apiService.getAssetsByIdentifier(this.identifierType, identifierValue, this.assetFormConfigId);
        if (assets.length === 0) {
            this.presentNoAssetFoundAlert(identifierValue);
        } else {
            if (assets.length === 1) {
                const asset = assets[0];
                const { id, asset_form_config_id, identifiers: { asset_identifier } } = asset;
                const toAssetFormConfigComparisonId = this.props.asset_form_config_id?.split('::v')[0];
                const assetFormConfigComparisonId = asset_form_config_id.split('::v')[0];

                if (this.scannedAssets.find(a => a.asset_identifier.toLowerCase().trim() === asset_identifier.toLowerCase().trim())) {
                    await this.presentAlreadyScannedAlert();
                } else {
                    await this.confirmAsset(id, asset);
                }
            } else {
                this.presentMultiAssetInformationModal(assets);
            }
        }
    }

    private async confirmAsset(assetId: string, asset: IAsset) {
        const assetIdentifier: any = asset.sections[0].questions[0];
        const scannedAsset: IScannedAsset = {
            asset_id: assetId,
            asset_identifier: assetIdentifier.answer,
            label: assetIdentifier.label,
            value: assetIdentifier.label + ': ' + assetIdentifier.answer,
            sections: [{
                questions: asset.sections[0].questions.filter(question => question.showInList && question.answer)
            }]
        };
        this.scannedAssets.unshift(scannedAsset);
        this.saveAssets();
    }

    private async presentNoAssetFoundAlert(identifierValue) {
        let message = '';
        if (this.identifierType === 'rfid_tag') {
            message = 'This RFID tag has not been recognised.\nPlease try scanning again.';
        } else if (this.identifierType === 'qr_code') {
            message = 'This QR Code has not been recognised.\nPlease try scanning again.';
        } else if (this.identifierType === 'uhf_rfid_tag') {
            message = 'This UHF RFID Tag has not been recognised.\nPlease try again.';
        } else if (this.identifierType === 'asset_identifier') {
            message = this.assetLabel + ' \'' + identifierValue + '\' has not been recognised.\nPlease try entering the ' + this.assetLabel + ' of another asset of type ' + this.title + '.';
        }

        const noAssetFoundAlert = await this.alertController.create({
            header: 'Cannot find project',
            message,
            buttons: ['Ok']
        });
        await noAssetFoundAlert.present();
    }

    private async presentAlreadyScannedAlert() {
        const assetAlreadyScannedAlert = await this.alertController.create({
            header: 'Project already added',
            message: 'This project has already been added. Please try with another project.',
            buttons: [{
                text: 'Ok'
            }]
        });
        await assetAlreadyScannedAlert.present();
    }

    private saveAssets() {
        if (this.scannedAssets?.length > 0) {
            this.projectsEvent.emit(this.scannedAssets);
        } else {
            this.projectsEvent.emit(null);
        }

    }
}
